<template>
	<div
		v-if="hasEditPermissions && dropdownItems.length > 0"
		class="text-right"
		:class="{ 'margin-left-auto pr-2': !isLoop, 'is-mounted': isMounted }"
	>
		<div>
			<AppDropdownMenu absolute :items="dropdownItems" v-model="localValue">
				<template #activator>
					<RoundButton tabindex="-1" class="menu_btn mt-1" icon height="25" width="25">
						<v-icon small>fas fa-ellipsis-v</v-icon>
					</RoundButton>
				</template>
			</AppDropdownMenu>
		</div>
	</div>
</template>

<script>
import { mapGetters } from 'vuex';

import { ServiceSlugs } from '@ply-io/ply-front-shared-utilities';

import BlockPropsComputedsMixin from '../BlockPropsComputedsMixin';
import { FeatureTypes } from '@/components/Billing/billing-types';
import AppDialogManagerService from '@/components/dialogs/AppDialogManager/app-dialog-manager-service';
import VModelMixin from '@/mixins/VModelMixin';
import { getAction } from '@/store/modules/actions/getters';
import { getServiceBySlug } from '@/store/modules/apps/getters';
import { closeSideBar } from '@/store/modules/selectedNode/actions';
import { duplicateStep } from '@/store/modules/selectedWorkflow/actions';
import { selectedWorkflowId, editWorkflow } from '@/store/modules/selectedWorkflow/getters';
import { markMoveStepLocationInTreeActive } from '@/store/modules/treeBlocks/actions';
import { isMoveNodeActive, nodeIdToMove, useWorkflowVersionGraph } from '@/store/modules/treeBlocks/getters';
import { setIsSelectingWorkflowRoute, setRootNodeOfRouteSelection, setWorkflowGraphs } from '@/store/modules/workflowGraphs/actions';
import { hasWorkflowEditPermissions, canEditWorkflow, rootNodeId as rootNodeIdGetter } from '@/store/modules/workflows/getters';
import { mapActionsShorthand, mapGettersShorthand } from '@/store/utils/mappers';
import { arrayToObject } from '@/utils/helpers';
import * as WorkflowGraphService from '@/utils/workflow/workflow-graph-service';

export default {
	name: 'TreeBlockMenu',
	props: {
		blockData: { type: Object, required: true },
		isFirstBlock: { type: Boolean },
		isContextualBlock: { type: Boolean },
	},
	mixins: [BlockPropsComputedsMixin, VModelMixin],
	data() {
		return {
			isMounted: false,
		};
	},
	computed: {
		...mapGetters([hasWorkflowEditPermissions.fullPath, selectedWorkflowId.fullPath]),
		...mapGettersShorthand({
			isMoveNodeActive,
			nodeIdToMove,
			useWorkflowVersionGraph,
			canEditWorkflow,
			rootNodeIdGetter,
			editWorkflow,
			getServiceBySlug,
			getAction,
		}),
		hasEditPermissions() {
			return this.canEditWorkflow(this[selectedWorkflowId.fullPath]);
		},
		endRunService() {
			return this.getServiceBySlug(ServiceSlugs.EndRun);
		},
		rootNodeId() {
			return this.rootNodeIdGetter(this.editWorkflow.id, this.workflowVersionId);
		},
		workflowVersionId() {
			return this.blockData.workflow_version;
		},
		isLastEndRunBlock() {
			const lastNode = WorkflowGraphService.getLastReachableNodeInTreeThatIsNotInBranch(this.mainPathGraph.nodes, this.mainPathGraph.edges);
			return lastNode?.id === this.blockData.id && lastNode?.service === this.endRunService?.id;
		},
		workflowVersionNodesMap() {
			return arrayToObject(this.workflowVersionNodes);
		},

		workflowVersionNodes() {
			return this.workflowVersionGraph.nodes || [];
		},
		workflowVersionEdgesMap() {
			return arrayToObject(this.workflowVersionEdges);
		},
		workflowVersionEdges() {
			return this.workflowVersionGraph.edges || [];
		},
		dag() {
			return WorkflowGraphService.createWorkflowGraph(this.workflowVersionNodes, this.workflowVersionEdges, this.getAction);
		},
		mainPathGraph() {
			const mainGraphPath = WorkflowGraphService.iterateFlat(this.dag, String(this.rootNodeId));
			const nodes = this.workflowVersionNodes.filter((node) => mainGraphPath.includes(String(node.id)));
			const nodeIds = nodes.map((node) => node.id);
			const edges = this.workflowVersionEdges.filter((edge) => nodeIds.includes(edge.source) && nodeIds.includes(edge.target));
			return { edges, nodes };
		},
		dropdownItems() {
			const { isFirstBlock, isLoop, isContextualBlock, canStepMove, canDuplicate, showDeleteOption, isFinalEndRunBlock } = this;

			const RENAME_ITEM = { text: 'Rename', onClick: () => this.$emit('editName') };
			const MOVE_ITEM_TO = { text: 'Move to...', onClick: this.moveStep };
			const DUPLICATE_ITEM = { text: 'Duplicate step', onClick: this.duplicateThisStep };
			const DELETE_ITEM = { text: 'Delete Step', onClick: this.deleteStep };
			const SKIP_ITEM = { text: 'Skip', onClick: this.skipNode };
			const CREATE_SUBFLOW = {
				text: 'Convert steps into a subflow…',
				onClick: this.initConvertToSubflow,
				billingFeatureId: FeatureTypes.SubFlows,
			};

			const dropdownItems = [];
			if (isFirstBlock) {
				return dropdownItems;
			}
			if (!isLoop && !isContextualBlock) {
				dropdownItems.push(RENAME_ITEM);
			}
			if (!isFinalEndRunBlock) {
				if (canStepMove) {
					dropdownItems.push(MOVE_ITEM_TO);
				}
				if (canDuplicate) {
					dropdownItems.push(DUPLICATE_ITEM);
				}
				if (showDeleteOption) {
					dropdownItems.push(DELETE_ITEM);
				}
				dropdownItems.push({ divider: true, text: 'Multi-step actions' });
				dropdownItems.push(CREATE_SUBFLOW);
			}
			if (isContextualBlock) {
				dropdownItems.push(SKIP_ITEM);
			}
			return dropdownItems;
		},
		actionName() {
			return this.actionData?.title;
		},
		appVersion() {
			return this.blockData.service_version;
		},
		showDeleteOption() {
			return !this.isFirstBlock && !this.isConsolidateDataNode && !this.isContextualBlock;
		},
		canStepMove() {
			return !this.isLoop && !this.isBranches && !this.isContextualBlock && !this.isLastEndRunBlock;
		},
		canDuplicate() {
			return this.canStepMove;
		},
		workflowVersionGraph() {
			return this.useWorkflowVersionGraph(this.blockData.workflow, this.blockData.workflow_version);
		},
	},
	methods: {
		...mapActionsShorthand({
			markMoveStepLocationInTreeActive,
			setIsSelectingWorkflowRoute,
			setRootNodeOfRouteSelection,
			duplicateStep,
			setWorkflowGraphs,
			closeSideBar,
		}),

		deleteStep() {
			if (this.blockData.temp) {
				this.$emit('remove-temp');
				return;
			}
			AppDialogManagerService.openDeleteBlockDialog({ block: this.blockData });
		},
		moveStep() {
			this.markMoveStepLocationInTreeActive({ nodeId: this.blockData.id });
		},
		duplicateThisStep() {
			this.duplicateStep({ nodeId: this.blockData.id });
		},
		skipNode() {
			this.$emit('skipNode', { node: this.blockData });
		},
		initConvertToSubflow() {
			this.setIsSelectingWorkflowRoute(true);
			this.setRootNodeOfRouteSelection(this.blockData.id);
			this.setWorkflowGraphs({ nodes: this.workflowVersionGraph.nodes || [], edges: this.workflowVersionGraph.edges || [] });
			this.closeSideBar({ avoidRedirect: true });
		},
	},
	mounted() {
		setTimeout(() => {
			this.isMounted = true;
		}, 100);
	},
};
</script>

<style lang="scss" scoped>
@import '../styles/choose-node-bg.scss';

.menu_btn {
	opacity: 0;
	transition: all 0.3s linear 0.1s;
	transform: scale(0.95);
	transform-origin: center center;

	i {
		font-size: 13px !important;
	}
}
.is-mounted .menu_btn {
	opacity: 1;
	transform: scale(1);
	transition: all 0.3s linear 0.1s;
}
</style>
